import Link from "next/link";
import Meta from "@/components/meta";
import Navbar from "@/components/navbar";
import Landing from "@/components/jobs/landing";
import ChevronRight from "@/components/icons/chevron_right";
import Gallery from "@/components/jobs/gallery";
import Footer from "@/components/footer";
import { getJobs, JobListing } from "@/lib/jobsApi";
import groupBy from "lodash/groupBy";
import kebabCase from "lodash/kebabCase";

interface EngPostProps {
  href: string;
  title: string;
  author: string;
}
const engPosts: Array<EngPostProps> = [
  {
    title: "Creating a shareable Earth Day campaign in 1 week",
    href: "creating-a-shareable-earth-day-campaign-in-1-week",
    author: "Lachlan Campbell",
  },
  {
    title: "The challenges of async work: Temporal @ Watershed",
    href: "https://youtu.be/Tx3c3pmLPdw",
    author: "David Clark, Jessica Zhu",
  },
  {
    title: "Incremental data calculation in React",
    href: "incremental-calculation",
    author: "Carl Jackson",
  },
  {
    title: "The Day 1 ship at Watershed",
    href: "the-day-one-ship",
    author: "Jessica Zhu",
  },
  {
    title: "Evolving MDX publishing with a headless CMS",
    href: "evolving-mdx-publishing-with-a-headless-cms",
    author: "Lachlan Campbell",
  },
];

function EngPost({ href, title, author }: EngPostProps) {
  return (
    <Link href={href.startsWith("http") ? href : `/eng/${href}`} passHref>
      <a>
        <strong>{title}</strong>
        <ChevronRight />
        <small>{author}</small>
        <style jsx>{`
          a {
            border-radius: 8px;
            padding: 12px 16px;
            display: grid;
            grid-template-columns: 1fr auto;
            column-gap: 8px;
            line-height: 1.375;
            align-items: center;
            font-weight: bold;
          }
          a:hover,
          a:focus {
            background-color: #eef7ff;
            text-decoration: none;
          }
          a:hover strong,
          a:focus strong {
            text-decoration: underline;
          }
          small {
            color: #6d7384;
            font-weight: normal;
          }
        `}</style>
      </a>
    </Link>
  );
}

export default function Jobs({
  jobsByDepartment,
}: {
  jobsByDepartment: Record<string, Array<JobListing>>;
}) {
  return (
    <>
      <Meta title="Jobs" />
      <Navbar />
      <main>
        <Landing />
        <article className="open-roles">
          <header className="padded-box">
            <h2>Roles</h2>
            <p>
              We’re deeply committed to an equitable recruiting process and an
              inclusive culture that welcomes individuals across all races,
              ages, abilities, sexualities, gender identities/expressions,
              ethnicities, nationalities, and class backgrounds. This is
              especially critical in the climate space, which heavily intersects
              questions of equity and social justice.{" "}
            </p>
          </header>
          {Object.keys(jobsByDepartment)
            .sort()
            .map((department) => (
              <div
                id={kebabCase(department)}
                className="on-page-anchor"
                key={department}
              >
                <h3>{department}</h3>
                <ul>
                  {jobsByDepartment[department].map((job) => (
                    <li key={job.slug}>
                      <Link href={`/jobs/${job.slug}`}>
                        <a>
                          <strong>{job.title}</strong>
                          <small>
                            {job.location?.replace(
                              ", California, United States",
                              ""
                            )}
                          </small>
                          <ChevronRight />
                        </a>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
        </article>
        <aside className="sidebar">
          <section className="padded-box reading-list">
            <h2>Climate reading</h2>
            <p>
              <Link href="/reading-list">
                <a>These are some of our favorite climate readings</a>
              </Link>
              , ones that have been influential in our work at Watershed. Please
              send recommendations or feedback as you read.
            </p>
          </section>
          <span className="rule" />
          <section className="eng-writing padded-box">
            <h2>Writings from engineering & design</h2>
            <ul>
              {engPosts.map((post) => (
                <li key={post.title}>
                  <EngPost {...post} />
                </li>
              ))}
            </ul>
          </section>
          <Gallery />
        </aside>
      </main>
      <Footer />
      <style jsx>{`
        main {
          display: grid;
          gap: 24px;
        }

        @media (min-width: 820px) {
          main {
            grid-template-columns: 3fr 2fr;
            align-items: start;
          }
          :global(.intro) {
            grid-column: span 2;
          }
          .open-roles {
            grid-row: span 2;
          }
          .sidebar {
            grid-column: 2;
            grid-row: 2;
          }
          :global(.gallery) {
            grid-column: 2;
            grid-row: 3;
          }
        }

        h2 {
          font-size: 24px;
        }
        p:last-child {
          margin-bottom: 0;
        }

        .open-roles {
          border: 2px solid #edf1f5;
          border-radius: 16px;
        }
        .open-roles h3 {
          padding: 8px 26px 2px;
          font-size: 16px;
          color: #6d7384;
          margin: 0;
        }
        .open-roles ul {
          list-style: none;
          padding: 0 8px 8px;
        }
        .open-roles li {
          border-radius: 8px;
          padding: 12px 18px;
          margin: 0;
          max-width: none;
        }
        .open-roles li:nth-child(odd) {
          background-color: #eef7ff;
        }
        .open-roles li a {
          display: grid;
          grid-template-columns: 1fr auto;
          align-items: center;
          column-gap: 12px;
          text-decoration: none;
        }
        .open-roles li :global(svg) {
          grid-row: 1;
          grid-column: -1;
        }
        .open-roles li a:hover strong,
        .open-roles li a:focus strong {
          text-decoration: underline;
        }
        .open-roles li a small {
          color: #6d7384;
          font-weight: normal;
          text-decoration: none !important;
        }

        .sidebar .padded-box {
          padding: 32px 24px;
        }
        .sidebar .rule {
          display: block;
          background-color: #e0f0ff;
          width: calc(100% - 48px);
          height: 1px;
          margin: 0 auto;
        }

        .eng-writing ul {
          list-style: none;
          padding: 0;
          margin-left: -16px;
        }
        .eng-writing li {
          margin: 0;
        }

        @media (max-width: 520px) {
          .open-roles li a small {
            grid-row: 2;
          }
        }
        @media (min-width: 520px) {
          .open-roles .padded-box {
            padding-bottom: 24px;
          }
          .open-roles li a {
            grid-template-columns: 3fr 2fr auto;
          }
          .open-roles li a small {
            text-align: right;
          }

          .sidebar .padded-box {
            padding: 48px 32px;
          }
          .eng-writing ul {
            margin-right: -32px;
          }
          .sidebar .rule {
            width: calc(100% - 64px);
          }
        }
        @media (min-width: 820px) {
          .open-roles li {
            padding: 12px 40px;
          }
          .open-roles h3 {
            padding: 12px 48px 4px;
          }
        }
      `}</style>
    </>
  );
}

export async function getStaticProps() {
  const jobs = await getJobs();
  const jobsByDepartment = groupBy(jobs, "department");
  return { props: { jobsByDepartment }, revalidate: 5 };
}
